import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Plus, FileText } from "lucide-react";
import { CoverPageDisplay } from "../quote-builder/CoverPageDisplay";
import { RichTextContent } from "../ui/RichTextEditor";
import { resolveImageUrl } from "./ContentBlocks";
import { BeforeAfterDisplay } from "./BeforeAfterDisplay";
import { computeQuoteDiscounts } from "../../lib/quoteDiscounts";
import { applyDeckingWorstCase } from "../../lib/deckingWorstCase";

export function CustomBlocksRenderer({ content }) {
  const blocks = (content && content.blocks) || [];
  if (blocks.length === 0) return <p className="text-sm text-slate-400 italic">Empty page</p>;
  return (
    <div className="space-y-3">
      {blocks.map((block, i) => {
        if (block.type === "text") {
          return (
            <p key={i} style={{
              fontFamily: block.fontFamily || "Inter", fontSize: `${block.fontSize || 14}px`,
              color: block.color || "#1e293b", textAlign: block.textAlign || "left",
              fontWeight: block.fontWeight || "normal", fontStyle: block.fontStyle || "normal",
              textDecoration: block.textDecoration || "none", lineHeight: 1.6, whiteSpace: "pre-line",
            }}>{block.content}</p>
          );
        }
        if (block.type === "spacer") return <div key={i} style={{ height: `${block.height || 20}px` }} />;
        if (block.type === "image") {
          const w = block.width || "full";
          const widthStyle = w === "full" ? "100%" : `${w}%`;
          const align = block.textAlign || "center";
          const wrapClass = align === "left" ? "flex justify-start" : align === "right" ? "flex justify-end" : "flex justify-center";
          const imgSrc = resolveImageUrl(block.url);
          return imgSrc ? (
            <div key={i} className={wrapClass}>
              <img src={imgSrc} alt={block.alt || ""} className="rounded-md object-contain" style={{ width: widthStyle, height: "auto" }} />
            </div>
          ) : null;
        }
        if (block.type === "before_after") return <BeforeAfterDisplay key={i} block={block} />;
        if (block.type === "html") {
          const htmlStr = typeof block.content === 'object' && block.content?.html ? block.content.html : block.content;
          return htmlStr ? <div key={i} dangerouslySetInnerHTML={{ __html: htmlStr }} /> : null;
        }
        if (block.type === "richtext") {
          const rtContent = typeof block.content === 'object' && block.content?.html ? block.content.html : block.content;
          return rtContent ? <RichTextContent key={i} content={rtContent} className="text-sm text-slate-700 leading-relaxed" /> : null;
        }
        if (block.type === "video") {
          const url = resolveImageUrl(block.url || "");
          let embedUrl = url;
          if (url.includes("youtube.com/watch")) embedUrl = url.replace("watch?v=", "embed/");
          else if (url.includes("youtu.be/")) embedUrl = `https://www.youtube.com/embed/${url.split("youtu.be/")[1]}`;
          else if (/vimeo\.com\/\d+/.test(url)) embedUrl = `https://player.vimeo.com/video/${url.split("vimeo.com/")[1].split(/[?#]/)[0]}`;
          // Direct file detection (mp4/mov/webm) → use <video>
          const isDirectFile = /\.(mp4|mov|webm|m4v|ogv)(\?|$)/i.test(url) || url.includes("/api/uploads/");
          if (isDirectFile) {
            return (
              <div key={i} className="space-y-1" data-testid="quote-video-block">
                <video controls playsInline src={url} className="w-full rounded-md bg-black border" preload="metadata" />
                {block.caption && <p className="text-xs text-slate-500 text-center">{block.caption}</p>}
              </div>
            );
          }
          return embedUrl ? (
            <div key={i} className="space-y-1">
              <div className="aspect-video rounded-md overflow-hidden bg-slate-100">
                <iframe src={embedUrl} title="Video" className="w-full h-full border-0" allowFullScreen />
              </div>
              {block.caption && <p className="text-xs text-slate-500 text-center">{block.caption}</p>}
            </div>
          ) : null;
        }
        if (block.type === "link") {
          return <div key={i} className="text-center"><a href={block.url || "#"} target="_blank" rel="noopener noreferrer" className="inline-block px-6 py-2 bg-[#0F4C81] text-white rounded-md text-sm font-medium hover:bg-[#0D4270]">{block.label || "Link"}</a></div>;
        }
        if (block.type === "columns") {
          const colCount = block.columns || 2;
          const colData = block.column_data || [];
          return (
            <div key={i} className={`grid gap-4 ${colCount === 2 ? "grid-cols-2" : colCount === 3 ? "grid-cols-3" : "grid-cols-4"}`}>
              {Array.from({ length: colCount }).map((_, ci) => (
                <div key={ci} className="text-sm text-slate-600 whitespace-pre-line">{colData[ci]?.content || ""}</div>
              ))}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

export function PagePreviewRenderer({ page, allPages, settings, quote }) {
  const content = page.content || {};
  const pt = page.page_type;

  if (pt === "cover") {
    return (
      <CoverPageDisplay
        headline={content.headline || "PROPOSAL"}
        subheadline={content.subheadline || ""}
        logoUrl={content.logo_url || ""}
        bgImageUrl={content.bg_image_url || ""}
        bgColor={content.bgColor || "#0F4C81"}
        companyName={settings?.company_name || ""}
        companyPhone={settings?.company_phone || ""}
        companyEmail={settings?.company_email || ""}
        companyAddress={settings?.company_address || ""}
        contactName="Customer Name"
        projectAddress="Project Address"
        quoteNumber="Quote #Q-XXXX-0001"
        showLogo={content.show_logo !== false}
        showDate={content.show_date !== false}
        showQuoteNumber={content.show_quote_number !== false}
        showContactInfo={content.show_contact_info !== false}
        showCompanyInfo={content.show_company_info !== false}
      />
    );
  }

  if (pt === "option") {
    // Calculate pricing breakdown from line items.
    // Rule: a set base_price IS the final customer-facing total — no tax layered on.
    // Only itemized pages (no base_price) add tax on top of the items sum.
    const lineItems = content.line_items || [];
    const calcItemTotal = (it) => {
      const qty = parseFloat(it.quantity) || 0;
      const price = parseFloat(it.unit_price) || 0;
      const margin = parseFloat(it.margin) || 0;
      const waste = parseFloat(it.waste_factor) || 0;
      return qty * (1 + waste / 100) * price * (1 + margin / 100);
    };
    const manualPrice = parseFloat(content.base_price) || 0;
    const hasManualPrice = manualPrice > 0;
    const subtotal = hasManualPrice ? manualPrice : lineItems.reduce((sum, li) => sum + calcItemTotal(li), 0);
    const taxRate = hasManualPrice ? 0 : (parseFloat(content.tax_rate) || 0);
    const tax = subtotal * (taxRate / 100);
    const totalBeforeDiscount = subtotal + tax;
    const showCash = content.show_cash_discount !== false && content.show_cash_discount;
    const cashTotal = Math.round(totalBeforeDiscount * 0.98 * 100) / 100;

    return (
      <div className="border-2 border-[#0F4C81] rounded-xl overflow-hidden shadow-sm">
        <div className="bg-[#0F4C81] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-0.5">Option</p>
            <h2 className="text-2xl font-bold text-white">{content.option_name || "Option Name"}</h2>
          </div>
          {content.option_tag && <Badge className="bg-white/20 text-white border-white/30 text-sm px-3 py-1">{content.option_tag}</Badge>}
        </div>
        <div className="px-6 py-5 space-y-0 bg-white">
          {content.description && (
            <div className="pb-4">
              <p className="text-[10px] uppercase font-bold text-[#00A3E0] tracking-wider mb-2">Highlights</p>
              <RichTextContent content={content.description} className="leading-relaxed text-slate-700" />
            </div>
          )}
          {content.show_scope !== false && content.scope && (
            <div className="border-t border-slate-200 pt-4 pb-4">
              <p className="text-[10px] uppercase font-bold text-amber-600 tracking-wider mb-2">Scope of Work</p>
              <RichTextContent content={content.scope} className="leading-relaxed text-slate-700" />
            </div>
          )}
          {content.important_info && (
            <div className="border-t border-slate-200 pt-4 pb-4">
              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-md p-4">
                <p className="text-[10px] uppercase font-bold text-amber-700 tracking-wider mb-1">Important Information</p>
                <RichTextContent content={content.important_info} className="text-sm text-amber-800 leading-relaxed" />
              </div>
            </div>
          )}
          {content.show_warranty !== false && (content.warranty_material || content.warranty_wind) && (
            <div className="border-t border-slate-200 pt-4 pb-4">
              <div className="flex gap-3">
                {content.warranty_material && <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex-1 text-center"><p className="text-[10px] text-slate-400 uppercase font-bold tracking-wide">Material Warranty</p><p className="text-base font-bold text-slate-800 mt-1">{content.warranty_material}</p></div>}
                {content.warranty_wind && <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex-1 text-center"><p className="text-[10px] text-slate-400 uppercase font-bold tracking-wide">Wind Warranty</p><p className="text-base font-bold text-slate-800 mt-1">{content.warranty_wind}</p></div>}
              </div>
            </div>
          )}
          {/* Price Breakdown */}
          {content.show_price !== false && subtotal > 0 && (() => {
            // Apply quote-level discounts to this option's total so the
            // figure shown here matches the customer view, sign page, and PDF.
            const { totalRemoved, rows: discountRows } = computeQuoteDiscounts(totalBeforeDiscount, quote);
            const totalAfterDiscount = Math.max(0, totalBeforeDiscount - totalRemoved);
            const cashTotalAfter = Math.round(totalAfterDiscount * 0.98 * 100) / 100;
            return (
            <div className="border-t border-slate-200 pt-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Subtotal</span>
                  <span className="font-medium">${subtotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
                {taxRate > 0 && (
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Tax ({taxRate}%)</span>
                    <span className="font-medium">${tax.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {discountRows.map((d, i) => (
                  <div key={i} className="flex justify-between text-sm text-rose-600">
                    <span>{d.label}</span>
                    <span className="font-medium">- ${d.removed.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
                {showCash && (
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Cash / Check Discount (2%)</span>
                    <span className="font-medium text-red-500">- ${(totalAfterDiscount - cashTotalAfter).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold text-[#0F4C81] border-t border-slate-200 pt-2">
                  <span>Total</span>
                  <span>${totalAfterDiscount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
                {showCash && (
                  <div className="flex justify-between items-center bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5 -mx-1">
                    <span className="font-semibold text-emerald-800 text-sm">Total with Cash/Check Discount</span>
                    <span className="font-extrabold text-emerald-700 text-lg">${cashTotalAfter.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>
            </div>
            );
          })()}
        </div>
      </div>
    );
  }

  if (pt === "addon") {
    const lineItems = content.line_items || [];
    const calcItemTotal = (it) => {
      const qty = parseFloat(it.quantity) || 0;
      const price = parseFloat(it.unit_price) || 0;
      const margin = parseFloat(it.margin) || 0;
      const waste = parseFloat(it.waste_factor) || 0;
      return qty * (1 + waste / 100) * price * (1 + margin / 100);
    };
    const subtotal = parseFloat(content.price) || lineItems.reduce((sum, li) => sum + calcItemTotal(li), 0);
    const taxRate = parseFloat(content.tax_rate) || 0;
    const tax = subtotal * (taxRate / 100);
    const total = subtotal + tax;
    const showCash = content.show_cash_discount !== false && content.show_cash_discount;
    const cashTotal = Math.round(total * 0.98 * 100) / 100;

    return (
      <div className="border-2 border-emerald-400 rounded-xl overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-white" />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Add-on Upgrade</p>
              <h2 className="text-lg font-bold text-white">{content.addon_name || "Add-on"}</h2>
            </div>
          </div>
        </div>
        {content.description && (
          <div className="px-6 py-4 bg-emerald-50/30">
            <RichTextContent content={content.description} className="text-sm text-slate-700 leading-relaxed" />
          </div>
        )}
        {/* Price Breakdown */}
        {content.show_price !== false && subtotal > 0 && (
          <div className="px-6 py-4 border-t border-emerald-100">
            <div className="bg-emerald-50/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm text-slate-600">
                <span>Subtotal</span>
                <span className="font-medium">+ ${subtotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
              {taxRate > 0 && (
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Tax ({taxRate}%)</span>
                  <span className="font-medium">${tax.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {showCash && (
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Cash / Check Discount (2%)</span>
                  <span className="font-medium text-red-500">- ${(total - cashTotal).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold text-emerald-700 border-t border-emerald-200 pt-2">
                <span>Total</span>
                <span>+ ${total.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
              {showCash && (
                <div className="flex justify-between items-center bg-emerald-100/60 border border-emerald-200 rounded-lg px-4 py-2.5 -mx-1">
                  <span className="font-semibold text-emerald-800 text-sm">Total with Cash/Check Discount</span>
                  <span className="font-extrabold text-emerald-700 text-lg">+ ${cashTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (pt === "text") {
    return (
      <div className="space-y-2">
        {content.title && <h2 className="text-lg font-bold text-[#0F4C81]">{content.title}</h2>}
        {content.body && <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{content.body}</p>}
      </div>
    );
  }

  if (pt === "custom") {
    return <CustomBlocksRenderer content={content} />;
  }

  if (pt === "gallery") {
    const images = (content.image_urls || "").split("\n").map(u => u.trim()).filter(Boolean);
    return (
      <div className="space-y-3">
        {content.title && <h2 className="text-lg font-bold text-[#0F4C81]">{content.title}</h2>}
        {images.length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            {images.map((url, i) => <img key={i} src={resolveImageUrl(url)} alt={`Gallery ${i+1}`} className="w-full aspect-square object-cover rounded-md" />)}
          </div>
        ) : <p className="text-sm text-slate-400 italic">No photos added</p>}
      </div>
    );
  }

  if (pt === "file") {
    return (
      <div className="flex items-center gap-3 bg-slate-50 rounded-md p-4 border border-slate-200">
        <FileText className="w-8 h-8 text-[#0F4C81]" />
        <div>
          <p className="text-sm font-medium text-slate-700">{content.file_name || "Attachment"}</p>
          {content.description && <p className="text-xs text-slate-500">{content.description}</p>}
        </div>
      </div>
    );
  }

  if (pt === "signature") {
    const optionPages = (allPages || []).filter(p => p.page_type === "option");
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-[#0F4C81]">Acceptance</h2>
        <p className="text-sm text-slate-600">By signing below, you accept the terms and pricing of this proposal.</p>
        {content.show_options !== false && optionPages.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-1">Selected Option:</p>
            {optionPages.map((op, i) => <p key={i} className="text-sm text-slate-600">[ ] {op.content?.option_name || op.title || "Option"}</p>)}
          </div>
        )}
        {content.show_questions !== false && content.questions && (
          <div className="space-y-2">
            {content.questions.split("\n").filter(q => q.trim()).map((q, i) => (
              <div key={i} className="flex gap-2 text-sm"><span className="text-slate-600">{q.trim()}:</span><span className="flex-1 border-b border-slate-300" /></div>
            ))}
          </div>
        )}
        <div className="space-y-4 pt-4">
          <div><p className="text-xs text-slate-400 mb-1">{content.customer_label || "Customer Signature"}</p><div className="border-b-2 border-slate-300 h-10" />{content.show_printed_name !== false && <><p className="text-xs text-slate-400 mt-2 mb-1">Printed Name</p><div className="border-b border-slate-200 h-6" /></>}</div>
          <div><p className="text-xs text-slate-400 mb-1">{content.admin_label || "Company Signature"}</p><div className="border-b-2 border-slate-300 h-10" />{content.show_printed_name !== false && <><p className="text-xs text-slate-400 mt-2 mb-1">Printed Name</p><div className="border-b border-slate-200 h-6" /></>}</div>
        </div>
      </div>
    );
  }

  if (pt === "structure") {
    if (!content.notes || content.notes === "<p></p>") return null;
    const notesHtml = applyDeckingWorstCase(content.notes, quote?.roof_total_sqft);
    return (
      <div className="bg-gradient-to-r from-[#0F4C81] to-[#0F4C81]/90 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-white font-bold text-lg">{content.title || "Structure"}</span>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
          <RichTextContent content={notesHtml} className="text-white/90 text-sm leading-relaxed [&_p]:text-white/90" />
        </div>
      </div>
    );
  }

  if (pt === "form") {
    const fields = (content.fields || "").split("\n").filter(f => f.trim());
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-bold text-[#0F4C81]">{content.title || "Form"}</h2>
        {fields.map((f, i) => {
          const [label, type] = f.split("|").map(s => s.trim());
          return (
            <div key={i} className="space-y-1">
              <Label className="text-xs text-slate-500">{label}</Label>
              {type === "textarea" ? <div className="border border-slate-200 rounded-md h-16 bg-white" /> : <div className="border border-slate-200 rounded-md h-9 bg-white" />}
            </div>
          );
        })}
      </div>
    );
  }

  if (pt === "financing") {
    return <FinancingPagePreview content={content} />;
  }

  // Fallback: any unrecognised page_type that still has block content (e.g.
  // legacy "design_layout" pages from older templates). Treat it as a custom
  // page so the photos / text / videos the user added still render on both
  // preview and the public customer view.
  if (content && Array.isArray(content.blocks) && content.blocks.length > 0) {
    return <CustomBlocksRenderer content={content} />;
  }
  return <p className="text-sm text-slate-400 italic">Unsupported page type: {pt}</p>;
}

function FinancingPagePreview({ content }) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-slate-800">{content.title || "Financing Options"}</h2>
      {content.intro && <p className="text-sm text-slate-600">{content.intro}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* HFS Card */}
        <div className="border-2 border-emerald-200 rounded-xl overflow-hidden bg-gradient-to-b from-emerald-50 to-white">
          <div className="bg-emerald-600 px-4 py-3">
            <p className="text-white font-bold text-sm">HFS Financial</p>
            <p className="text-emerald-100 text-xs">Funded Directly to You</p>
          </div>
          <div className="p-4 space-y-2">
            <p className="text-xs text-slate-600">{content.hfs_desc || "Funds deposited directly to you, the homeowner. Starts with a soft credit pull."}</p>
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">Soft Pull</span>
              <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">Flexible Terms</span>
            </div>
            {content.hfs_link && (
              <a href={content.hfs_link} target="_blank" rel="noopener noreferrer"
                className="inline-block mt-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors">
                Check Your Rate
              </a>
            )}
          </div>
        </div>

        {/* Wisetack Card */}
        <div className="border-2 border-blue-200 rounded-xl overflow-hidden bg-gradient-to-b from-blue-50 to-white">
          <div className="bg-[#0F4C81] px-4 py-3">
            <p className="text-white font-bold text-sm">Wisetack</p>
            <p className="text-blue-200 text-xs">Funded Directly to Contractor</p>
          </div>
          <div className="p-4 space-y-2">
            <p className="text-xs text-slate-600">{content.wisetack_desc || "Funds go directly to your contractor. Starts with a soft credit pull."}</p>
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Soft Pull</span>
              <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Instant Decision</span>
            </div>
            {content.wisetack_link && (
              <a href={content.wisetack_link} target="_blank" rel="noopener noreferrer"
                className="inline-block mt-2 px-4 py-2 bg-[#0F4C81] hover:bg-[#0D4270] text-white text-xs font-bold rounded-lg transition-colors">
                Check Your Rate
              </a>
            )}
          </div>
        </div>
      </div>

      {content.show_calculator !== false && (
        <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
          <p className="text-xs text-slate-400">Financing calculator will display on customer view</p>
        </div>
      )}
    </div>
  );
}
